import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { Share2 } from 'lucide-react';
import { hives, feed, recipes as recipesApi, shared } from '../utils/api';
import { useFeedContext } from '../contexts/FeedContext';
import BottomNav from '../components/BottomNav';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import HiveCard from '../components/HiveCard';
import BuzzCard from '../components/BuzzCard';
import { 
  trackHiveCreated, 
  trackHiveJoined, 
  trackBuzzLiked, 
  trackBuzzCommented,
  trackRecipeSavedFromFeed,
  trackFeedViewed 
} from '../utils/analytics';

// Icons
const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
  </svg>
);

const CloseIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const EditIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const HeartIcon = ({ filled, size = 18 }) => (
  <svg className={`${filled ? 'fill-red-500 text-red-500' : ''}`} width={size} height={size} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const CommentIcon = ({ size = 18 }) => (
  <svg width={size} height={size} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
  </svg>
);

const BookmarkIcon = ({ filled, size = 18 }) => (
  <svg width={size} height={size} fill={filled ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
);

const ShareIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
  </svg>
);

const CopyIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
  </svg>
);

// Clipboard fallback for environments where Clipboard API is blocked
const copyToClipboard = async (text) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (e) {
    // Fall through to fallback
  }
  
  // Fallback using textarea
  const textArea = document.createElement('textarea');
  textArea.value = text;
  textArea.style.position = 'fixed';
  textArea.style.left = '-999999px';
  textArea.style.top = '-999999px';
  document.body.appendChild(textArea);
  textArea.focus();
  textArea.select();
  
  try {
    document.execCommand('copy');
    textArea.remove();
    return true;
  } catch (e) {
    textArea.remove();
    return false;
  }
};

const FeedPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get cached data and fetch functions from context
  const {
    hiveData,
    popularHives: cachedPopularHives,
    appSettings: cachedAppSettings,
    loading: contextLoading,
    fetchHiveData,
    fetchPopularHives,
    fetchAppSettings,
    fetchFeedPosts,
    getCachedFeedPosts,
    updateFeedPostInCache,
    updateHiveUnreadStatus,
    invalidateHiveCache
  } = useFeedContext();
  
  const [currentUserId, setCurrentUserId] = useState(null);
  // Restore tab from navigation state (e.g., when coming back from HiveDetailPage)
  const [activeTab, setActiveTab] = useState(location.state?.tab || 'feed');
  
  // Local feed posts state (current view) - populated from cache or fetch
  // Initialize from cache immediately to avoid "No posts yet" flash
  const [feedPosts, setFeedPosts] = useState(() => getCachedFeedPosts(null, 'recent') || []);
  const [isInitializing, setIsInitializing] = useState(true); // Track first load
  const hasEverLoadedPosts = useRef(false); // Track if we've ever successfully loaded posts
  const [selectedHive, setSelectedHive] = useState(null);
  const [selectedFeedFilter, setSelectedFeedFilter] = useState('all');  // 'all' or 'private'
  const [myHivesFilter, setMyHivesFilter] = useState('all');  // 'all', 'created', 'private', 'public'
  const [discoverTab, setDiscoverTab] = useState('popular');  // 'popular', 'foryou', 'private'
  const [sortBy, setSortBy] = useState('recent');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditHiveModal, setShowEditHiveModal] = useState(false);
  const [editingHive, setEditingHive] = useState(null);
  const [showCommentModal, setShowCommentModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [savingPost, setSavingPost] = useState(null);
  const [privateInviteCode, setPrivateInviteCode] = useState('');  // For joining private hives
  const [showDormantHives, setShowDormantHives] = useState(false);
  const [openHiveMenu, setOpenHiveMenu] = useState(null);  // Track which hive's menu is open
  const [newlyCreatedHive, setNewlyCreatedHive] = useState(null);  // Show share modal after creating
  const [showQueenManager, setShowQueenManager] = useState(false);  // Queen management panel
  const [queenData, setQueenData] = useState({ queens: [], queen_count: 0, queen_invite_code: null });
  const [loadingQueens, setLoadingQueens] = useState(false);
  const [userRecipeMap, setUserRecipeMap] = useState(new Map()); // source_recipe_id -> recipe_id
  const [userRecipeIds, setUserRecipeIds] = useState(new Set()); // Set of recipe_ids user has (their own IDs)
  const seenHiveIds = useRef(new Set()); // Track which hives have been "seen" via scrolling in feed
  
  // Derive values from context cache
  const myHives = hiveData.active;
  const allHives = hiveData.all_hives;
  const myCreatedHives = hiveData.my_created;
  const joinedHives = hiveData.joined;
  const privateHives = hiveData.private;
  const publicHives = hiveData.public;
  const dormantHives = hiveData.dormant;
  const unreadCount = hiveData.unread_count;
  const unreadPrivateCount = hiveData.unread_private_count;
  const popularHives = cachedPopularHives;
  const appSettings = cachedAppSettings;
  
  // Show loading while initializing
  const loading = isInitializing;
  
  // Only show empty state if:
  // 1. We're done initializing AND
  // 2. We've actually attempted to load posts (hasEverLoadedPosts) AND  
  // 3. There are truly no posts OR user has no hives
  // Show "Sharing is Easy!" for both cases since it explains how to create a hive
  const showSharingIsEasyEmpty = !isInitializing && hasEverLoadedPosts.current && (feedPosts.length === 0 || myHives.length === 0);
  
  // Mark hive as read when a post from it becomes visible
  const handlePostVisible = useCallback(async (post) => {
    const postHiveIds = post.hive_ids || (post.hives?.map(h => h.hive_id) || []);
    
    for (const hiveId of postHiveIds) {
      if (!seenHiveIds.current.has(hiveId)) {
        seenHiveIds.current.add(hiveId);
        try {
          await hives.markRead(hiveId);
          // Update context cache to clear blue dot
          updateHiveUnreadStatus(hiveId, false);
        } catch (error) {
          console.error(`Failed to mark hive ${hiveId} as read:`, error);
        }
      }
    }
  }, [updateHiveUnreadStatus]);
  
  // Return to Buzz flow state - when user came from Buzz modal to find hives
  const [returnToBuzzRecipe, setReturnToBuzzRecipe] = useState(null);
  const [savedBuzzState, setSavedBuzzState] = useState(null);
  const [returnToGroceryShare, setReturnToGroceryShare] = useState(false);
  
  // Check if we came from Buzz modal or Grocery share looking for hives
  useEffect(() => {
    if (location.state?.returnToBuzz && location.state?.buzzRecipeId) {
      setReturnToBuzzRecipe({
        recipeId: location.state.buzzRecipeId,
        recipeTitle: location.state.buzzRecipeTitle
      });
      // Save the buzz state to pass back when returning
      if (location.state?.buzzState) {
        setSavedBuzzState(location.state.buzzState);
      }
      // Set to discover tab if specified
      if (location.state?.tab === undefined) {
        setActiveTab('discover');
      }
      // Clean up the state to prevent it persisting on refresh
      window.history.replaceState({}, '', '/feed');
    }
    
    // Check if we came from Grocery share modal
    if (location.state?.returnToGroceryShare) {
      setReturnToGroceryShare(true);
      // Set to discover tab
      setActiveTab('discover');
      // Clean up the state
      window.history.replaceState({}, '', '/feed');
    }
  }, [location.state]);
  
  // Load user's recipes to build the source_recipe_id -> recipe_id map
  // Uses lightweight endpoint that returns only IDs (not full recipe data)
  const loadUserRecipes = useCallback(async () => {
    try {
      const res = await recipesApi.getIdMapping();
      const recipes = res.data || [];
      
      // Build map from source_recipe_id to user's recipe_id
      // Also build a set of all recipe_ids the user owns
      const recipeMap = new Map();
      const allRecipeIds = new Set();
      recipes.forEach(r => {
        // Add user's own recipe_id to the set
        allRecipeIds.add(r.recipe_id);
        
        // Map source_recipe_id -> recipe_id for saved copies
        if (r.source_recipe_id) {
          recipeMap.set(r.source_recipe_id, r.recipe_id);
        }
        // Also map recipe_id -> recipe_id for user's own recipes
        // This handles the case where user opens their own buzzed recipe
        recipeMap.set(r.recipe_id, r.recipe_id);
      });
      setUserRecipeMap(recipeMap);
      setUserRecipeIds(allRecipeIds);
    } catch (error) {
      console.error('Error loading user recipes for mapping:', error);
    }
  }, []);
  
  const [newHive, setNewHive] = useState({
    name: '',
    description: '',
    icon: '🐝',
    hive_type: 'private',  // Default to private
    posting_mode: 'open',  // 'open' (all can post) or 'queens' (curated by creator)
    tags: ''  // Space/comma separated tags
  });

  // Get current user ID from localStorage
  useEffect(() => {
    const loadCurrentUser = async () => {
      // First try localStorage cache
      const userCache = localStorage.getItem('userCache');
      if (userCache) {
        try {
          const user = JSON.parse(userCache);
          setCurrentUserId(user.user_id);
          return;
        } catch (e) {
          console.error('Error parsing user cache:', e);
        }
      }
      // Fallback to API
      try {
        const response = await fetch(`${window.location.origin}/api/auth/me`, {
          credentials: 'include'
        });
        if (response.ok) {
          const user = await response.json();
          setCurrentUserId(user.user_id);
        }
      } catch (e) {
        console.error('Error fetching current user:', e);
      }
    };
    loadCurrentUser();
  }, []);

  // Initialize likedPosts from feedPosts data when currentUserId is available
  useEffect(() => {
    if (currentUserId && feedPosts.length > 0) {
      const userLikedPosts = new Set();
      feedPosts.forEach(post => {
        if (post.likes?.includes(currentUserId)) {
          userLikedPosts.add(post.buzz_id);
        }
      });
      if (userLikedPosts.size > 0) {
        setLikedPosts(userLikedPosts);
      }
    }
  }, [currentUserId, feedPosts]);

  // Load data using context (with caching)
  const loadData = useCallback(async (forceRefresh = false) => {
    // Fetch all data in parallel - context handles caching
    const [hiveDataResult] = await Promise.all([
      fetchHiveData(forceRefresh),
      fetchPopularHives(forceRefresh),
      fetchAppSettings(forceRefresh)
    ]);
    
    // Load feed posts if user has hives
    const activeHives = hiveDataResult?.active || [];
    if (activeHives.length > 0) {
      // Check for cached feed posts first
      const cachedPosts = getCachedFeedPosts(selectedHive, sortBy);
      if (cachedPosts && cachedPosts.length > 0 && !forceRefresh) {
        setFeedPosts(cachedPosts);
        hasEverLoadedPosts.current = true;
      }
      // Fetch fresh data (will update cache)
      const posts = await fetchFeedPosts(selectedHive, sortBy, forceRefresh);
      setFeedPosts(posts);
      hasEverLoadedPosts.current = true;
    } else {
      // User has no hives - we've confirmed this
      hasEverLoadedPosts.current = true;
    }
    
    // Mark initialization complete
    setIsInitializing(false);
  }, [fetchHiveData, fetchPopularHives, fetchAppSettings, fetchFeedPosts, getCachedFeedPosts, selectedHive, sortBy]);

  // Initial data load
  useEffect(() => {
    loadData();
    loadUserRecipes();
  }, [loadData, loadUserRecipes]);

  // Close hive menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenHiveMenu(null);
    if (openHiveMenu) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [openHiveMenu]);

  // Emoji picker useEffect removed

  const loadFeed = useCallback(async (hiveId = null) => {
    // Show cached data immediately if available
    const cachedPosts = getCachedFeedPosts(hiveId, sortBy);
    if (cachedPosts) {
      setFeedPosts(cachedPosts);
    }
    // Fetch fresh data in background
    const posts = await fetchFeedPosts(hiveId, sortBy);
    setFeedPosts(posts);
  }, [fetchFeedPosts, getCachedFeedPosts, sortBy]);

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }
    try {
      const res = await hives.search(query);
      setSearchResults(res.data);
    } catch (error) {
      console.error('Error searching hives:', error);
    }
  };

  const handleJoinHive = async (hiveId) => {
    try {
      await hives.join(hiveId);
      trackHiveJoined(hiveId);
      toast.success('Joined hive!');
      invalidateHiveCache(hiveId);
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join hive');
    }
  };

  const handleLeaveHive = async (hiveId) => {
    try {
      await hives.leave(hiveId);
      toast.success('Left hive');
      invalidateHiveCache(hiveId);
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to leave hive');
    }
  };

  const handleDeleteHive = async (hiveId) => {
    if (!window.confirm('Are you sure you want to delete this hive? This cannot be undone.')) {
      return;
    }
    try {
      await hives.delete(hiveId);
      toast.success('Hive deleted');
      invalidateHiveCache(hiveId);
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete hive');
    }
  };

  const handleCreateHive = async () => {
    if (!newHive.name.trim()) {
      toast.error('Hive name is required');
      return;
    }
    try {
      // Parse tags from string to array
      const tagsArray = newHive.tags
        .split(/[\s,]+/)
        .map(t => t.trim().toLowerCase().replace(/^#/, ''))
        .filter(t => t.length > 0);
      
      const response = await hives.create({
        ...newHive,
        tags: tagsArray
      });
      
      // Store the created hive to show share modal
      const createdHive = response.data;
      
      // Track hive creation
      trackHiveCreated(createdHive.hive_id, createdHive.name, createdHive.hive_type === 'private');
      
      setShowCreateModal(false);
      setNewlyCreatedHive(createdHive);
      setNewHive({ name: '', description: '', icon: '🐝', hive_type: 'private', posting_mode: 'open', tags: '' });
      invalidateHiveCache(createdHive.hive_id);
      loadData(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create hive');
    }
  };

  // Handle joining private hive via invite code
  const handleJoinPrivateHive = async () => {
    if (!privateInviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }
    try {
      const res = await hives.getByInviteCode(privateInviteCode);
      if (res.data && !res.data.is_member) {
        await hives.join(res.data.hive_id, privateInviteCode);
        toast.success(`Joined ${res.data.name}!`);
        setPrivateInviteCode('');
        invalidateHiveCache(res.data.hive_id);
        loadData(true);
        setActiveTab('myhives');  // Switch to My Hives after joining
      } else if (res.data?.is_member) {
        toast.info('Already a member of this hive');
      }
    } catch (error) {
      toast.error('Invalid invite code');
    }
  };

  const handleHiveFilter = (hiveId) => {
    setSelectedHive(hiveId);
    loadFeed(hiveId);
  };

  // Open edit modal for a hive
  const openEditHiveModal = async (hive) => {
    const tagsString = (hive.tags || []).join(' ');
    setEditingHive({...hive, tagsString});
    setShowEditHiveModal(true);
    setShowQueenManager(false);
    setQueenData({ queens: [], queen_count: 0, queen_invite_code: null });
    
    // Load queen data if it's a queen's hive
    if (hive.posting_mode === 'queens') {
      setLoadingQueens(true);
      try {
        const res = await hives.getQueens(hive.hive_id);
        setQueenData(res.data);
      } catch (err) {
        console.error('Failed to load queens:', err);
      } finally {
        setLoadingQueens(false);
      }
    }
  };

  const handleLikePost = async (postId) => {
    const isLiked = likedPosts.has(postId);
    
    // Optimistically update UI
    setLikedPosts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(postId)) {
        newSet.delete(postId);
      } else {
        newSet.add(postId);
      }
      return newSet;
    });
    
    // Update post like count in feedPosts
    setFeedPosts(prev => prev.map(p => {
      if (p.buzz_id === postId) {
        const newLikes = isLiked 
          ? (p.likes || []).filter(id => id !== currentUserId)
          : [...(p.likes || []), currentUserId];
        return {
          ...p,
          likes: newLikes,
          like_count: newLikes.length
        };
      }
      return p;
    }));
    
    // Call API
    try {
      if (isLiked) {
        await feed.unlikePost(postId);
      } else {
        await feed.likePost(postId);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert on error
      setLikedPosts(prev => {
        const newSet = new Set(prev);
        if (isLiked) {
          newSet.add(postId);
        } else {
          newSet.delete(postId);
        }
        return newSet;
      });
    }
  };

  const handleSaveRecipe = async (post) => {
    if (!post.recipe_id) {
      toast.error('No recipe linked to this post');
      return;
    }
    
    // If it's the user's own post, just navigate to recipe
    if (post.user_id === currentUserId) {
      navigateToRecipeWithScroll(post.recipe_id);
      return;
    }
    
    setSavingPost(post.buzz_id);
    try {
      // Get the source_recipe_id from the post or from the recipe data
      const sourceId = post.source_recipe_id || post.recipe?.source_recipe_id;
      
      if (!sourceId) {
        toast.error('Cannot save this recipe');
        return;
      }
      
      const response = await shared.addToBox(sourceId);
      const recipeId = response.data.recipe_id;
      const alreadyHad = response.data.message === 'Recipe already in your box';
      
      // Show appropriate toast with "View Recipe" action
      if (alreadyHad) {
        toast.info('Recipe already in your box', {
          action: recipeId ? {
            label: 'View Recipe',
            onClick: () => navigateToRecipeWithScroll(recipeId)
          } : undefined,
          duration: 5000
        });
      } else {
        toast.success('Recipe added to your box!', {
          action: recipeId ? {
            label: 'View Recipe',
            onClick: () => navigateToRecipeWithScroll(recipeId)
          } : undefined,
          duration: 5000
        });
      }
    } catch (error) {
      toast.error('Failed to save recipe');
    } finally {
      setSavingPost(null);
    }
  };

  const handleViewRecipe = (post) => {
    // Save scroll position and tab before navigating
    sessionStorage.setItem('feedScrollY', window.scrollY.toString());
    sessionStorage.setItem('feedTab', activeTab);
    
    const params = new URLSearchParams({ fromFeed: 'true' });
    if (selectedHive) {
      params.set('hiveFilter', selectedHive);
    }
    
    // Check if user has this recipe in their box via source_recipe_id
    const sourceId = post.source_recipe_id || post.recipe?.source_recipe_id;
    
    if (sourceId && userRecipeMap.has(sourceId)) {
      // User has this recipe - navigate to their editable copy
      const userRecipeId = userRecipeMap.get(sourceId);
      navigate(`/recipe/${userRecipeId}?${params.toString()}`);
    } else if (sourceId) {
      // User doesn't have it - show the shared/read-only view
      navigate(`/shared/${sourceId}`, { 
        state: { fromFeed: true } 
      });
    } else if (post.recipe_id) {
      // Fallback: No source_recipe_id set (legacy recipe)
      // Navigate to the variant view which handles this gracefully
      navigate(`/shared/variant/${post.recipe_id}`, {
        state: { fromFeed: true }
      });
    }
  };

  // Helper to navigate to recipe with scroll position saved
  const navigateToRecipeWithScroll = (recipeId) => {
    sessionStorage.setItem('feedScrollY', window.scrollY.toString());
    sessionStorage.setItem('feedTab', activeTab);
    const params = new URLSearchParams({ fromFeed: 'true' });
    if (selectedHive) params.set('hiveFilter', selectedHive);
    navigate(`/recipe/${recipeId}?${params.toString()}`);
  };

  // Share public hive link
  const handleSharePublicHive = async (hive) => {
    // Use OG endpoint for sharing (better link previews)
    const ogShareUrl = `${window.location.origin}/api/og/hive/${hive.hive_id}`;
    const shareText = `Check out "${hive.name}" - a recipe share group on Honey! 🍯`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: hive.name,
          text: shareText,
          url: ogShareUrl
        });
      } catch (e) {
        // User cancelled or share failed, fall back to copy
        const success = await copyToClipboard(ogShareUrl);
        if (success) {
          toast.success('Hive link copied!');
        }
      }
    } else {
      const success = await copyToClipboard(ogShareUrl);
      if (success) {
        toast.success('Hive link copied!');
      } else {
        toast.error('Could not copy link');
      }
    }
  };

  // Share/invite to hive (handles both private and public)
  const handleShareHive = async (hive) => {
    if (hive.hive_type === 'private' && hive.invite_code) {
      // Private hive - share invite link
      const inviteUrl = `${window.location.origin}/join-hive?code=${hive.invite_code}`;
      const shareText = `You're invited to join "${hive.name}" - a private recipe share group on Honey! 🍯`;
      const fullShareContent = `${inviteUrl}\n${shareText}`;
      
      if (navigator.share) {
        try {
          await navigator.share({
            title: `Join ${hive.name}`,
            text: shareText,
            url: inviteUrl
          });
        } catch (e) {
          // User cancelled or share failed, fall back to copy
          const success = await copyToClipboard(fullShareContent);
          if (success) {
            toast.success('Invite link copied!');
          }
        }
      } else {
        const success = await copyToClipboard(fullShareContent);
        if (success) {
          toast.success('Invite link copied!');
        } else {
          toast.error('Could not copy link');
        }
      }
    } else {
      // Public hive - share hive link
      await handleSharePublicHive(hive);
    }
  };

  // Handle invite code, queen invite, hive filter, and tab from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const inviteCode = params.get('invite');
    const queenInviteCode = params.get('queen_invite');
    const hiveFilter = params.get('hive');
    const tab = params.get('tab');
    
    if (tab === 'discover') {
      setActiveTab('discover');
      window.history.replaceState({}, '', '/feed');
    }
    
    if (inviteCode) {
      // Clear the URL param
      window.history.replaceState({}, '', '/feed');
      // Try to join with invite code
      (async () => {
        try {
          const res = await hives.getByInviteCode(inviteCode);
          if (res.data) {
            if (res.data.is_member) {
              toast.info(`You're already a member of ${res.data.name}`);
            } else {
              await hives.join(res.data.hive_id, inviteCode);
              toast.success(`Joined ${res.data.name}! 🐝`);
              loadData(true);
            }
          }
        } catch (error) {
          toast.error('Invalid or expired invite link');
        }
      })();
    }
    
    // Handle queen invite code - joins as a Queen who can post
    if (queenInviteCode) {
      window.history.replaceState({}, '', '/feed');
      (async () => {
        try {
          const res = await hives.getByQueenInviteCode(queenInviteCode);
          if (res.data) {
            // Join as queen
            const joinRes = await hives.joinAsQueen(res.data.hive_id);
            if (joinRes.data.already_member) {
              toast.success(`👑 You're now a Queen of ${res.data.name}!`);
            } else {
              toast.success(`👑 Joined ${res.data.name} as a Queen! You can post recipes here.`);
            }
            loadData(true);
          }
        } catch (error) {
          toast.error('Invalid or expired queen invite link');
        }
      })();
    }
    
    if (hiveFilter) {
      // Restore hive filter and clean URL
      setSelectedHive(hiveFilter);
      loadFeed(hiveFilter);
      window.history.replaceState({}, '', '/feed');
    }
  }, []);

  const openCommentModal = (post) => {
    setSelectedPost(post);
    setShowCommentModal(true);
  };

  const handleAddComment = async () => {
    if (!newComment.trim() || !selectedPost) return;
    
    try {
      // TODO: API call to add comment
      // await buzz.addComment(selectedPost.buzz_id, newComment);
      toast.success('Comment added!');
      setNewComment('');
      // Refresh feed to show new comment
      loadFeed(selectedHive);
    } catch (error) {
      toast.error('Failed to add comment');
    }
  };

  const timeAgo = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d`;
    return date.toLocaleDateString();
  };

  // Check if post is user's own
  const isOwnPost = (post) => post.user_id === currentUserId;

  // Get filtered hives for My Hives tab based on selected filter
  const getMyHivesFiltered = () => {
    switch (myHivesFilter) {
      case 'created':
        return myCreatedHives;
      case 'private':
        return privateHives;
      case 'public':
        return publicHives;
      default:
        return allHives;
    }
  };

  // Get hives with posts only for My Feed filter bar (exclude empty and dormant)
  const getHivesWithPosts = () => {
    return myHives.filter(hive => hive.last_post_at && !hive.is_dormant);
  };

  // Navigate to hive detail page with scroll position
  const handleOpenHive = (hiveId) => {
    const scrollY = window.scrollY;
    navigate(`/hive/${hiveId}`, { 
      state: { from: '/feed', tab: activeTab, scrollY } 
    });
  };

  // Store pending scroll position from navigation state OR sessionStorage
  const pendingScrollRef = React.useRef(
    location.state?.scrollY ?? 
    (sessionStorage.getItem('feedScrollY') ? parseInt(sessionStorage.getItem('feedScrollY'), 10) : undefined)
  );
  const pendingTabRef = React.useRef(
    location.state?.tab ?? sessionStorage.getItem('feedTab')
  );

  // Restore scroll position and tab after data loads
  useEffect(() => {
    // Restore tab if coming back from recipe
    if (pendingTabRef.current && pendingTabRef.current !== activeTab) {
      setActiveTab(pendingTabRef.current);
      pendingTabRef.current = undefined;
      sessionStorage.removeItem('feedTab');
    }
  }, []);

  useEffect(() => {
    if (!loading && pendingScrollRef.current !== undefined) {
      // Wait for DOM to update after loading completes
      requestAnimationFrame(() => {
        window.scrollTo(0, pendingScrollRef.current);
        pendingScrollRef.current = undefined; // Clear after restoring
        sessionStorage.removeItem('feedScrollY'); // Clean up
      });
    }
  }, [loading]);

  // Unread indicator component
  const UnreadDot = () => (
    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white"></span>
  );

  return (
    <div className="min-h-screen bg-white pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-40">
        <div className="max-w-lg mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-slate-800">🐝 Feed</h1>
          </div>
          
          {/* Tabs - Now with 3 tabs */}
          <div className="flex mt-3 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('feed')}
              data-testid="feed-tab-my-feed"
              className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'feed'
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              My Feed
            </button>
            <button
              onClick={() => setActiveTab('myhives')}
              data-testid="feed-tab-my-hives"
              className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors relative ${
                activeTab === 'myhives'
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              My Hives
              {unreadCount > 0 && activeTab !== 'myhives' && (
                <span className="absolute top-1 right-4 w-2 h-2 bg-blue-500 rounded-full"></span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('discover')}
              data-testid="feed-tab-discover"
              className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'discover'
                  ? 'border-amber-600 text-amber-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Join Hives
            </button>
          </div>
        </div>
      </div>

      {/* Return to Buzz Banner - shows when user came from Buzz modal to find hives */}
      {returnToBuzzRecipe && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-3">
          <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xl flex-shrink-0">🐝</span>
              <p className="text-sm text-amber-800 truncate">
                Buzzing about <span className="font-medium">{returnToBuzzRecipe.recipeTitle || 'your recipe'}</span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => {
                  navigate(`/recipe/${returnToBuzzRecipe.recipeId}`, { 
                    state: { 
                      openBuzzModal: true,
                      buzzState: savedBuzzState 
                    } 
                  });
                }}
                className="px-3 py-1.5 bg-amber-600 text-white text-sm font-medium rounded-full hover:bg-amber-700 transition-colors"
              >
                Continue Buzz
              </button>
              <button
                onClick={() => setReturnToBuzzRecipe(null)}
                className="p-1 text-amber-600 hover:text-amber-800"
                title="Dismiss"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Return to Grocery Share banner */}
      {returnToGroceryShare && (
        <div className="bg-emerald-50 border-b border-emerald-200 px-4 py-3">
          <div className="max-w-lg mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-xl flex-shrink-0">🛒</span>
              <p className="text-sm text-emerald-800 truncate">
                Back to <span className="font-medium">sharing grocery list</span>
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              <button
                onClick={() => {
                  navigate('/groceries', { 
                    state: { openShareModal: true } 
                  });
                }}
                className="px-3 py-1.5 bg-emerald-600 text-white text-sm font-medium rounded-full hover:bg-emerald-700 transition-colors"
              >
                Continue
              </button>
              <button
                onClick={() => setReturnToGroceryShare(false)}
                className="p-1 text-emerald-600 hover:text-emerald-800"
                title="Dismiss"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content - remove max-w-lg for hive filter to be full width */}
      {activeTab === 'feed' ? (
        <>
          {/* Hive Filter Bar - Scrollable with blue dots for unread (only hives with posts) */}
          {getHivesWithPosts().length > 0 && (
            <div className="bg-white border-b border-gray-200 overflow-x-auto scrollbar-hide">
              <div className="flex gap-2 px-4 py-2 min-w-min justify-center">
                <button
                  onClick={() => { handleHiveFilter(null); setSelectedFeedFilter('all'); }}
                  data-testid="feed-filter-all"
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors flex-shrink-0 ${
                    selectedHive === null && selectedFeedFilter === 'all'
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All
                </button>
                {/* Private filter - only show if public hives enabled AND there are unread private posts */}
                {appSettings.public_hives_available && unreadPrivateCount > 0 && (
                  <button
                    onClick={() => { setSelectedHive(null); setSelectedFeedFilter('private'); loadFeed(null); }}
                    data-testid="feed-filter-private"
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors flex-shrink-0 relative ${
                      selectedFeedFilter === 'private'
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    🔒 Private
                    {selectedFeedFilter !== 'private' && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border border-white"></span>
                    )}
                  </button>
                )}
                {getHivesWithPosts().map(hive => (
                  <button
                    key={hive.hive_id}
                    onClick={() => { handleHiveFilter(hive.hive_id); setSelectedFeedFilter('all'); }}
                    data-testid={`feed-filter-hive-${hive.hive_id}`}
                    className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors flex-shrink-0 relative ${
                      selectedHive === hive.hive_id
                        ? 'bg-amber-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {hive.icon} {hive.name}
                    {hive.has_unread && selectedHive !== hive.hive_id && (
                      <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border border-white"></span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Sort and Posts in constrained container */}
          <div className="max-w-lg mx-auto">
            {/* Sort Toggle */}
            <div className="px-4 py-2 flex justify-end">
              <select
                value={sortBy}
                onChange={(e) => {
                  setSortBy(e.target.value);
                  loadFeed(selectedHive);
                }}
                className="text-sm border border-gray-300 rounded-lg px-2 py-1"
              >
                <option value="recent">Most Recent</option>
                <option value="trending">Trending</option>
              </select>
            </div>

            {/* Feed Posts */}
            <div className="px-4 space-y-2">
              {loading ? (
                <LoadingSpinner message="Loading your feed..." />
              ) : showSharingIsEasyEmpty ? (
                <div className="text-center py-12 px-6">
                  <div className="text-6xl mb-4">🍯</div>
                  <h3 className="font-semibold text-gray-800 mb-3">Sharing is Easy!</h3>
                  <div className="text-gray-600 text-sm space-y-2 max-w-xs mx-auto text-left">
                    <p>1. Use the <span className="font-medium">+</span> button below to create a Hive</p>
                    <p>2. Invite members to your Hive</p>
                    <p>3. From any recipe, tap <span className="font-medium">Buzz</span> to share a photo and comment to your Hive</p>
                    <p>4. Comment on posts from other members</p>
                  </div>
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="mt-6 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                  >
                    Create a Hive
                  </button>
                </div>
              ) : feedPosts.length === 0 ? (
                <LoadingSpinner message="Loading your feed..." />
              ) : (
                feedPosts.map(post => {
                  // If filtering by a specific hive, reorder hives to show filtered hive first
                  let displayPost = post;
                  if (selectedHive && post.hives?.length > 1) {
                    const filteredHiveIndex = post.hives.findIndex(h => h.hive_id === selectedHive);
                    if (filteredHiveIndex > 0) {
                      // Move the filtered hive to the front for display
                      const reorderedHives = [...post.hives];
                      const [filteredHive] = reorderedHives.splice(filteredHiveIndex, 1);
                      reorderedHives.unshift(filteredHive);
                      displayPost = { ...post, hives: reorderedHives };
                    }
                  }
                  return (
                    <BuzzCard
                      key={post.buzz_id}
                      post={displayPost}
                      variant="feed"
                      inlineComments={true}
                      onHiveClick={handleOpenHive}
                      onRecipeClick={handleViewRecipe}
                      onLike={handleLikePost}
                      onVisible={handlePostVisible}
                      onAddComment={async (buzzId, text) => {
                        try {
                          const response = await feed.addComment(buzzId, text);
                          // Update local state immediately with the new comment
                          if (response.data?.comment) {
                            setFeedPosts(prevPosts => prevPosts.map(p => {
                              if (p.buzz_id === buzzId) {
                                return {
                                  ...p,
                                  comments: [...(p.comments || []), response.data.comment],
                                  comment_count: (p.comment_count || 0) + 1
                                };
                              }
                              return p;
                            }));
                          }
                        } catch (error) {
                          throw error;
                        }
                      }}
                      onSave={handleSaveRecipe}
                      isLiked={likedPosts.has(post.buzz_id)}
                      isOwnPost={isOwnPost(post)}
                      saving={savingPost === post.buzz_id}
                    />
                  );
                })
              )}
            </div>
          </div>
        </>
      ) : activeTab === 'myhives' ? (
        /* My Hives Tab */
        <div className="max-w-lg mx-auto">
          {/* Filter Bar for My Hives */}
          <div className="bg-white border-b border-gray-200 overflow-x-auto scrollbar-hide">
            <div className="flex gap-2 px-4 py-2 min-w-min justify-center">
              {[
                { key: 'all', label: 'All' },
                { key: 'created', label: 'Created by me' },
                // Only show Private/Public filters if public hives are enabled
                ...(appSettings.public_hives_available ? [
                  { key: 'private', label: 'Private' },
                  { key: 'public', label: 'Public' }
                ] : [])
              ].map(filter => (
                <button
                  key={filter.key}
                  onClick={() => setMyHivesFilter(filter.key)}
                  data-testid={`myhives-filter-${filter.key}`}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap transition-colors flex-shrink-0 ${
                    myHivesFilter === filter.key
                      ? 'bg-amber-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>
          </div>

          {/* Hive List */}
          <div className="px-4 py-4 space-y-2">
            {loading ? (
              <LoadingSpinner message="Loading your hives..." />
            ) : getMyHivesFiltered().length === 0 ? (
              <EmptyState
                icon="🐝"
                title="No Hives Here"
                description={
                  myHivesFilter === 'created' 
                    ? "You haven't created any hives yet."
                    : myHivesFilter === 'private'
                    ? "You're not in any private hives."
                    : myHivesFilter === 'public'
                    ? "You're not in any public hives."
                    : "Join some hives to see them here!"
                }
                action={
                  <button
                    onClick={() => setActiveTab('discover')}
                    className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                  >
                    Discover Hives
                  </button>
                }
              />
            ) : (
              getMyHivesFiltered().map(hive => (
                <div
                  key={hive.hive_id}
                  className="bg-white rounded-xl border border-gray-100"
                  data-testid={`myhives-item-${hive.hive_id}`}
                >
                  <div className="p-3 flex items-center gap-3">
                    {/* Icon with unread dot */}
                    <div className="relative flex-shrink-0">
                      <span className="text-2xl">{hive.icon}</span>
                      {hive.has_unread && (
                        <span className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full border-2 border-white"></span>
                      )}
                    </div>
                    
                    {/* Hive Info - Title is clickable link */}
                    <div className="flex-1 min-w-0">
                      <button
                        onClick={() => handleOpenHive(hive.hive_id)}
                        className="font-medium text-slate-700 hover:text-amber-600 hover:underline truncate block text-left"
                      >
                        {hive.name}
                        {hive.hive_type === 'private' && (
                          <span className="ml-1 text-xs">🔒</span>
                        )}
                      </button>
                      <div className="text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1">
                          <UsersIcon /> {hive.member_count} members
                        </span>
                        <span className="text-gray-300">•</span>
                        <span>{hive.post_count || 0} recipes</span>
                        {hive.my_role === 'queen_bee' && (
                          <span className="text-amber-600">👑 Creator</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Menu Icon */}
                    <div className="relative flex-shrink-0">
                      <button
                        onClick={(e) => { 
                          e.stopPropagation(); 
                          setOpenHiveMenu(openHiveMenu === hive.hive_id ? null : hive.hive_id);
                        }}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
                        data-testid={`hive-menu-btn-${hive.hive_id}`}
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
                        </svg>
                      </button>
                      
                      {/* Dropdown Menu - positioned relative to the button */}
                      {openHiveMenu === hive.hive_id && (
                        <div 
                          className="absolute right-0 top-10 z-50 bg-white rounded-lg shadow-lg border border-gray-200 py-1 min-w-[140px]"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {hive.my_role === 'queen_bee' ? (
                            <>
                              <button
                                onClick={(e) => { 
                                  e.stopPropagation(); 
                                  setOpenHiveMenu(null);
                                  openEditHiveModal(hive); 
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-amber-50 flex items-center gap-2"
                              >
                                <EditIcon /> Edit
                              </button>
                              {hive.hive_type === 'private' && (
                                <button
                                  onClick={(e) => { 
                                    e.stopPropagation(); 
                                    setOpenHiveMenu(null);
                                    handleShareHive(hive); 
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-amber-50 flex items-center gap-2"
                                >
                                  <ShareIcon /> Invite
                                </button>
                              )}
                              <button
                                onClick={(e) => { 
                                  e.stopPropagation();
                                  e.preventDefault();
                                  const hiveIdToDelete = hive.hive_id;
                                  setOpenHiveMenu(null);
                                  // Use setTimeout to ensure menu closes before confirm dialog
                                  setTimeout(() => handleDeleteHive(hiveIdToDelete), 100);
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                              >
                                <TrashIcon /> Delete
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={(e) => { 
                                e.stopPropagation(); 
                                setOpenHiveMenu(null);
                                handleLeaveHive(hive.hive_id); 
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                            >
                              Leave Hive
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}

            {/* Dormant Hives Section */}
            {dormantHives.length > 0 && (
              <div className="mt-6">
                <button
                  onClick={() => setShowDormantHives(!showDormantHives)}
                  className="w-full flex items-center justify-between py-2 text-gray-600 hover:text-gray-800"
                >
                  <span className="text-sm font-medium">
                    💤 Dormant Hives ({dormantHives.length})
                  </span>
                  <svg 
                    className={`w-4 h-4 transition-transform ${showDormantHives ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                {showDormantHives && (
                  <div className="space-y-2 mt-2">
                    {dormantHives.map(hive => (
                      <div
                        key={hive.hive_id}
                        onClick={() => handleOpenHive(hive.hive_id)}
                        className="bg-gray-50 rounded-xl border border-gray-200 p-3 flex items-center justify-between cursor-pointer hover:bg-gray-100 hover:border-gray-300 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-xl opacity-50">{hive.icon}</span>
                          <div>
                            <span className="text-gray-600">{hive.name}</span>
                            <div className="text-xs text-gray-400">Inactive for 14+ days</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Join Hives Tab */
        <div className="max-w-lg mx-auto px-4 py-4 space-y-4">
          {/* Search - Only show if public hives are enabled */}
          {appSettings.public_hives_available && (
            <div className="relative">
              <input
                type="text"
                placeholder="Search hives..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-amber-500 focus:border-amber-600"
                data-testid="discover-search"
              />
              <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <SearchIcon />
              </div>
            </div>
          )}

          {/* Search Results - When searching (only if public enabled) */}
          {appSettings.public_hives_available && searchQuery && searchResults.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-semibold text-gray-700">Search Results</h3>
              {searchResults.map(hive => (
                <HiveCard
                  key={hive.hive_id}
                  hive={hive}
                  variant="discover"
                  onOpen={handleOpenHive}
                  onJoin={handleJoinHive}
                  showTags={true}
                  showUnread={false}
                />
              ))}
            </div>
          )}

          {/* Sub-tabs - Only when not searching */}
          {!searchQuery && (
            <>
              {/* Only show Popular/For You tabs if public hives are enabled */}
              {appSettings.public_hives_available ? (
                <div className="flex gap-2 overflow-x-auto scrollbar-hide justify-center">
                  {[
                    { key: 'popular', label: '🔥 Popular', icon: '' },
                    { key: 'foryou', label: '✨ For You', icon: '' },
                    { key: 'private', label: '🔒 Join Private', icon: '' }
                  ].map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setDiscoverTab(tab.key)}
                      data-testid={`discover-tab-${tab.key}`}
                      className={`px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors ${
                        discoverTab === tab.key
                          ? 'bg-amber-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {tab.label}
                    </button>
                  ))}
                </div>
              ) : null}

              {/* Popular Hives - Only show if public hives enabled */}
              {appSettings.public_hives_available && discoverTab === 'popular' && (
                <div className="space-y-2">
                  {loading ? (
                    <LoadingSpinner message="Loading popular hives..." />
                  ) : popularHives.length > 0 ? (
                    popularHives.map(hive => (
                      <HiveCard
                        key={hive.hive_id}
                        hive={hive}
                        variant="discover"
                        onOpen={handleOpenHive}
                        onJoin={handleJoinHive}
                        showTags={true}
                        showUnread={false}
                      />
                    ))
                  ) : (
                    <EmptyState
                      icon="🐝"
                      title="No public hives yet"
                      description="Create the first one!"
                      className="py-8"
                    />
                  )}
                </div>
              )}

              {/* For You Hives - Only show if public hives enabled */}
              {appSettings.public_hives_available && discoverTab === 'foryou' && (
                <div className="space-y-2">
                  <p className="text-sm text-gray-500 mb-3">Hives matching your cooking interests</p>
                  {popularHives.length > 0 ? (
                    // For now, show popular hives shuffled - later can be AI-powered
                    [...popularHives].sort(() => Math.random() - 0.5).slice(0, 5).map(hive => (
                      <HiveCard
                        key={hive.hive_id}
                        hive={hive}
                        variant="discover"
                        onOpen={handleOpenHive}
                        onJoin={handleJoinHive}
                        showTags={true}
                        showUnread={false}
                      />
                    ))
                  ) : (
                    <EmptyState
                      icon="✨"
                      title="No suggestions yet"
                      description="Join some hives to get personalized recommendations!"
                      className="py-8"
                    />
                  )}
                </div>
              )}

              {/* Join Private Hive - Always show when public disabled, or when private tab selected */}
              {(discoverTab === 'private' || !appSettings.public_hives_available) && (
                <div className="bg-white rounded-xl border border-gray-100 p-6">
                  <div className="text-center mb-4">
                    <p className="text-4xl mb-2">🔒</p>
                    <h3 className="font-semibold text-gray-800">Join a Private Hive</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Paste an invite link or enter a code from a friend
                    </p>
                  </div>
                  <div className="space-y-3">
                    <input
                      type="text"
                      placeholder="Paste invite link or code..."
                      value={privateInviteCode}
                      onChange={(e) => {
                        // Extract code from URL if pasted
                        const val = e.target.value;
                        const match = val.match(/invite=([A-Z0-9]+)/i);
                        setPrivateInviteCode(match ? match[1] : val);
                      }}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl text-center focus:ring-2 focus:ring-amber-500 focus:border-amber-600"
                      data-testid="private-invite-input"
                    />
                    <button
                      onClick={handleJoinPrivateHive}
                      disabled={!privateInviteCode.trim()}
                      className="w-full py-3 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="join-private-btn"
                    >
                      Join Hive
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          {/* Create Hive CTA */}
          <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-slate-800">Start Your Own Hive</h3>
                <p className="text-sm text-gray-600">Create a community around your favorite cuisine</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 bg-amber-600 text-white rounded-full text-sm font-medium hover:bg-amber-700"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Global Create Hive FAB - visible on all tabs, positioned to avoid comment inputs */}
      <div className="fixed bottom-36 right-4 z-30 pointer-events-auto">
        <button
          onClick={() => setShowCreateModal(true)}
          className="w-14 h-14 bg-amber-600 text-white rounded-full shadow-lg hover:bg-amber-700 flex items-center justify-center transition-colors"
          data-testid="create-hive-fab"
          title="Create a Hive"
        >
          <PlusIcon />
        </button>
      </div>

      {/* Create Hive Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 pb-20">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[85vh] flex flex-col">
            <div className="p-6 pb-2 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Create a Hive</h2>
                <button onClick={() => setShowCreateModal(false)} className="text-gray-500">
                  <CloseIcon />
                </button>
              </div>
            </div>
            
            <div className="p-6 pt-4 space-y-4 overflow-y-auto flex-1">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={newHive.name}
                  onChange={(e) => setNewHive({...newHive, name: e.target.value})}
                  placeholder="e.g., Family Recipes, Dinner Club"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={newHive.description}
                  onChange={(e) => setNewHive({...newHive, description: e.target.value})}
                  placeholder="What's this hive about?"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20 resize-none"
                />
              </div>
              
              <div className="flex gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                  <div className="relative">
                    {/* Simple emoji text input - leverages native keyboard emoji picker */}
                    <input
                      type="text"
                      value={newHive.icon}
                      onChange={(e) => {
                        // Get the last character/emoji entered (handles multi-codepoint emojis)
                        const val = e.target.value;
                        if (val.length > 0) {
                          // Extract just the last emoji (supports multi-codepoint)
                          const emojis = [...val];
                          const lastEmoji = emojis[emojis.length - 1];
                          setNewHive({...newHive, icon: lastEmoji});
                        }
                      }}
                      className="w-16 h-12 px-3 py-2 border border-gray-300 rounded-lg text-center text-2xl hover:bg-gray-50"
                      placeholder="🐝"
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">Tap to use emoji keyboard</p>
                </div>
                
                <div className="flex-1">
                  {/* Only show Type selector if public hives are available */}
                  {appSettings.public_hives_available ? (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        value={newHive.hive_type}
                        onChange={(e) => setNewHive({...newHive, hive_type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                      >
                        <option value="public">🌍 Public</option>
                        <option value="private">🔒 Private</option>
                      </select>
                    </>
                  ) : (
                    <>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-700">
                        🔒 Private
                      </div>
                    </>
                  )}
                </div>
              </div>
              
              {/* Posting Mode - Open Hive vs Queen's Hive */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Who can post?</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="posting_mode"
                      value="open"
                      checked={newHive.posting_mode === 'open'}
                      onChange={(e) => setNewHive({...newHive, posting_mode: e.target.value})}
                      className="mt-1 text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">🐝 Open Hive</div>
                      <div className="text-xs text-gray-500">All members can post recipes and updates</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="posting_mode"
                      value="queens"
                      checked={newHive.posting_mode === 'queens'}
                      onChange={(e) => setNewHive({...newHive, posting_mode: e.target.value})}
                      className="mt-1 text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">👑 Queen's Hive</div>
                      <div className="text-xs text-gray-500">Only you (the creator) can post. Great for food bloggers!</div>
                    </div>
                  </label>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (optional)</label>
                <input
                  type="text"
                  value={newHive.tags}
                  onChange={(e) => setNewHive({...newHive, tags: e.target.value})}
                  placeholder="keto italian quick (space or comma separated)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                {newHive.tags && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {newHive.tags.split(/[\s,]+/).filter(t => t.trim()).map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                        #{tag.toLowerCase().replace(/^#/, '')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Sticky footer with Create button */}
            <div className="p-4 border-t bg-gray-50 flex-shrink-0">
              <button
                onClick={handleCreateHive}
                className="w-full py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700"
              >
                Create Hive
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Hive Modal */}
      {showEditHiveModal && editingHive && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 pb-20">
          <div className="bg-white rounded-xl w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white p-6 pb-2 border-b z-10">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold text-gray-900">Edit Hive</h2>
                <button onClick={() => { setShowEditHiveModal(false); setEditingHive(null); }} className="text-gray-500">
                  <CloseIcon />
                </button>
              </div>
            </div>
            
            <div className="p-6 pt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={editingHive.name}
                  onChange={(e) => setEditingHive({...editingHive, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={editingHive.description || ''}
                  onChange={(e) => setEditingHive({...editingHive, description: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg h-20 resize-none"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Icon</label>
                <input
                  type="text"
                  value={editingHive.icon}
                  onChange={(e) => setEditingHive({...editingHive, icon: e.target.value})}
                  className="w-16 px-3 py-2 border border-gray-300 rounded-lg text-center text-2xl"
                  maxLength={2}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags</label>
                <input
                  type="text"
                  value={editingHive.tagsString || ''}
                  onChange={(e) => setEditingHive({...editingHive, tagsString: e.target.value})}
                  placeholder="keto italian quick"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
                {editingHive.tagsString && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {editingHive.tagsString.split(/[\s,]+/).filter(t => t.trim()).map((tag, i) => (
                      <span key={i} className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs rounded-full">
                        #{tag.toLowerCase().replace(/^#/, '')}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Posting Mode - Open Hive vs Queen's Hive */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Who can post?</label>
                <div className="space-y-2">
                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="edit_posting_mode"
                      value="open"
                      checked={(editingHive.posting_mode || 'open') === 'open'}
                      onChange={(e) => setEditingHive({...editingHive, posting_mode: e.target.value})}
                      className="mt-1 text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">🐝 Open Hive</div>
                      <div className="text-xs text-gray-500">All members can post recipes and updates</div>
                    </div>
                  </label>
                  <label className="flex items-start gap-3 p-3 border border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                    <input
                      type="radio"
                      name="edit_posting_mode"
                      value="queens"
                      checked={(editingHive.posting_mode || 'open') === 'queens'}
                      onChange={(e) => setEditingHive({...editingHive, posting_mode: e.target.value})}
                      className="mt-1 text-amber-600 focus:ring-amber-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">👑 Queen&apos;s Hive</div>
                      <div className="text-xs text-gray-500">Only Queens can post</div>
                    </div>
                  </label>
                </div>
              </div>
              
              {/* Queen Management - Only show for Queen's Hives */}
              {(editingHive.posting_mode || 'open') === 'queens' && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="font-medium text-gray-900">👑 Manage Queens</h3>
                      <p className="text-xs text-gray-500">
                        {queenData.queen_count === 0 
                          ? 'Only you can post. Invite co-hosts to be Queens!'
                          : `${queenData.queen_count} Queen${queenData.queen_count !== 1 ? 's' : ''} + you`}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={async () => {
                        setShowQueenManager(!showQueenManager);
                        if (!showQueenManager && !queenData.queen_invite_code) {
                          setLoadingQueens(true);
                          try {
                            const res = await hives.getQueens(editingHive.hive_id);
                            setQueenData(res.data);
                          } catch (err) {
                            console.error('Failed to load queens:', err);
                          } finally {
                            setLoadingQueens(false);
                          }
                        }
                      }}
                      className="text-sm text-amber-600 hover:text-amber-700"
                    >
                      {showQueenManager ? 'Hide' : 'Manage'}
                    </button>
                  </div>
                  
                  {showQueenManager && (
                    <div className="space-y-3 pt-3 border-t border-gray-200">
                      {/* Generate/Show Queen Invite Link */}
                      <div>
                        <p className="text-sm font-medium text-gray-700 mb-2">Queen Invite Link</p>
                        {queenData.queen_invite_code ? (
                          <div className="flex gap-2">
                            <input
                              type="text"
                              readOnly
                              value={`${window.location.origin}/join-hive?queen=${queenData.queen_invite_code}`}
                              className="flex-1 px-3 py-2 text-xs bg-white border border-gray-300 rounded-lg"
                            />
                            <button
                              type="button"
                              onClick={async () => {
                                const url = `${window.location.origin}/join-hive?queen=${queenData.queen_invite_code}`;
                                try {
                                  await navigator.clipboard.writeText(url);
                                  toast.success('Queen invite link copied!');
                                } catch {
                                  toast.info(
                                    <div>
                                      <p className="font-medium mb-1">Share this link:</p>
                                      <p className="text-xs break-all select-all bg-gray-100 p-2 rounded">{url}</p>
                                    </div>,
                                    { duration: 15000 }
                                  );
                                }
                              }}
                              className="px-3 py-2 bg-amber-600 text-white text-sm rounded-lg hover:bg-amber-700"
                            >
                              Copy
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const res = await hives.generateQueenInvite(editingHive.hive_id);
                                setQueenData(prev => ({ ...prev, queen_invite_code: res.data.queen_invite_code }));
                                toast.success('Queen invite link generated!');
                              } catch (err) {
                                toast.error('Failed to generate invite link');
                              }
                            }}
                            className="w-full px-3 py-2 bg-gray-100 text-slate-700 text-sm rounded-lg hover:bg-gray-200"
                          >
                            Generate Queen Invite Link
                          </button>
                        )}
                        <p className="text-xs text-gray-500 mt-1">
                          Anyone who joins with this link becomes a Queen
                        </p>
                      </div>
                      
                      {/* List of Queens */}
                      {queenData.queens.length > 0 && (
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-2">Current Queens</p>
                          <div className="space-y-2">
                            {queenData.queens.map((queen) => (
                              <div key={queen.user_id} className="flex items-center justify-between bg-white rounded-lg p-2">
                                <div className="flex items-center gap-2">
                                  {queen.user_picture ? (
                                    <img src={queen.user_picture} alt="" className="w-8 h-8 rounded-full" />
                                  ) : (
                                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-slate-700 text-sm">
                                      {(queen.user_name || 'Q')[0].toUpperCase()}
                                    </div>
                                  )}
                                  <span className="text-sm text-gray-900">{queen.user_name}</span>
                                </div>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    if (confirm(`Remove ${queen.user_name} as Queen?`)) {
                                      try {
                                        await hives.removeQueen(editingHive.hive_id, queen.user_id);
                                        setQueenData(prev => ({
                                          ...prev,
                                          queens: prev.queens.filter(q => q.user_id !== queen.user_id),
                                          queen_count: prev.queen_count - 1
                                        }));
                                        toast.success('Queen removed');
                                      } catch (err) {
                                        toast.error('Failed to remove queen');
                                      }
                                    }
                                  }}
                                  className="text-xs text-red-500 hover:text-red-600"
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {loadingQueens && (
                        <div className="text-center py-2">
                          <div className="animate-spin rounded-full h-5 w-5 border-2 border-amber-600 border-t-transparent mx-auto"></div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              
              <button
                onClick={async () => {
                  try {
                    const tagsArray = (editingHive.tagsString || '')
                      .split(/[\s,]+/)
                      .map(t => t.trim().toLowerCase().replace(/^#/, ''))
                      .filter(t => t.length > 0);
                    
                    await hives.update(editingHive.hive_id, {
                      name: editingHive.name,
                      description: editingHive.description,
                      icon: editingHive.icon,
                      tags: tagsArray,
                      posting_mode: editingHive.posting_mode || 'open'
                    });
                    toast.success('Hive updated!');
                    setShowEditHiveModal(false);
                    setEditingHive(null);
                    loadData(true);
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Failed to update hive');
                  }
                }}
                className="w-full py-3 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700"
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Comment Modal */}

      {showCommentModal && selectedPost && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center" onClick={() => setShowCommentModal(false)}>
          <div 
            className="bg-white w-full sm:max-w-md sm:rounded-xl rounded-t-xl max-h-[80vh] overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Comments</h3>
              <button onClick={() => setShowCommentModal(false)} className="text-gray-500">
                <CloseIcon />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[50vh]">
              {selectedPost.comments?.length > 0 ? (
                <div className="space-y-3">
                  {selectedPost.comments.map((comment, idx) => (
                    <div key={idx} className="flex gap-2">
                      <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-slate-700 font-bold text-xs flex-shrink-0">
                        {comment.user_name?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <span className="font-medium text-sm">{comment.user_name}</span>
                        <p className="text-sm text-gray-700">{comment.text}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No comments yet. Be the first!</p>
              )}
            </div>
            
            <div className="p-4 border-t border-gray-200">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                />
                <button
                  onClick={handleAddComment}
                  disabled={!newComment.trim()}
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm font-medium disabled:bg-gray-300"
                >
                  Post
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hive Created Success Modal with Share Options */}
      {newlyCreatedHive && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 pb-20">
          <div className="bg-white rounded-xl w-full max-w-md p-6 max-h-[85vh] overflow-y-auto">
            <div className="text-center mb-6">
              <div className="text-5xl mb-3">{newlyCreatedHive.icon || '🐝'}</div>
              <h2 className="text-xl font-bold text-gray-900 mb-1">Hive Created!</h2>
              <p className="text-gray-600">{newlyCreatedHive.name}</p>
              {newlyCreatedHive.hive_type === 'private' && (
                <span className="inline-block mt-2 px-2 py-1 bg-amber-50 text-amber-700 text-xs rounded-full border border-amber-200">
                  🔒 Private Hive
                </span>
              )}
            </div>
            
            <div className="space-y-3">
              {/* Share/Invite Link */}
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  {newlyCreatedHive.hive_type === 'private' ? 'Invite Link' : 'Share Link'}
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    readOnly
                    value={
                      newlyCreatedHive.hive_type === 'private'
                        ? `${window.location.origin}/join-hive?code=${newlyCreatedHive.invite_code}`
                        : `${window.location.origin}/hive/${newlyCreatedHive.hive_id}`
                    }
                    className="flex-1 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-600"
                  />
                  <button
                    onClick={async () => {
                      const url = newlyCreatedHive.hive_type === 'private'
                        ? `${window.location.origin}/join-hive?code=${newlyCreatedHive.invite_code}`
                        : `${window.location.origin}/hive/${newlyCreatedHive.hive_id}`;
                      const success = await copyToClipboard(url);
                      if (success) {
                        toast.success('Link copied!');
                      } else {
                        toast.error('Failed to copy');
                      }
                    }}
                    className="px-3 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                    title="Copy link"
                  >
                    <CopyIcon />
                  </button>
                </div>
              </div>
              
              {/* Native Share Button */}
              {typeof navigator.share === 'function' && (
                <button
                  onClick={async () => {
                    const url = newlyCreatedHive.hive_type === 'private'
                      ? `${window.location.origin}/join-hive?code=${newlyCreatedHive.invite_code}`
                      : `${window.location.origin}/hive/${newlyCreatedHive.hive_id}`;
                    const title = newlyCreatedHive.hive_type === 'private'
                      ? `Join ${newlyCreatedHive.name} on Honey`
                      : `${newlyCreatedHive.name} on Honey`;
                    const text = newlyCreatedHive.hive_type === 'private'
                      ? `You're invited to join "${newlyCreatedHive.name}" - a private recipe share group on Honey! 🍯`
                      : `Check out "${newlyCreatedHive.name}" - a recipe share group on Honey! 🍯`;
                    
                    try {
                      await navigator.share({ title, text, url });
                    } catch (err) {
                      if (err.name !== 'AbortError') {
                        toast.error('Failed to share');
                      }
                    }
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-medium transition-colors"
                >
                  <Share2 size={20} />
                  <span>Share {newlyCreatedHive.hive_type === 'private' ? 'Invite' : 'Hive'}</span>
                </button>
              )}
              
              {/* Done Button */}
              <button
                onClick={() => setNewlyCreatedHive(null)}
                className="w-full px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
};

export default FeedPage;
